import { useEffect, useState, useCallback } from "react";
import api, { errMsg } from "../lib/api";
import { fmtIDR } from "../lib/format";
import { PageHeader, Card, Badge, Modal, Field, Btn, inputCls } from "../components/common";
import { useAuth } from "../context/AuthContext";
import { Plus, Trash2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

const MODULE_LABELS = { dashboard: "Dashboard", akuntansi: "Akuntansi", crm: "CRM", purchase: "Purchase", proyek: "Proyek", absensi: "Absensi", dokumen: "Dokumen", approval: "Approve & Sign", sales: "Sales Mobile", todo: "To Do List", kalender: "Kalender", inventory: "Inventory", penjualan: "Penjualan", settings: "Pengaturan" };
const ACTION_LABELS = { view: "Lihat", create: "Tambah", edit: "Ubah", delete: "Hapus", approve: "Approve", sign: "Sign" };

export default function Settings() {
  const { hasPerm, user } = useAuth();
  const [tab, setTab] = useState("users");
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [stages, setStages] = useState([]);
  const [rules, setRules] = useState([]);
  const [meta, setMeta] = useState({ modules: [], actions: [] });
  const [geofence, setGeofence] = useState({ lat: -6.2088, lng: 106.8456, radius_m: 200, label: "" });
  const [showUser, setShowUser] = useState(false);
  const [showRole, setShowRole] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [editingRole, setEditingRole] = useState(null);
  const [userForm, setUserForm] = useState({ name: "", email: "", password: "", role: "Sales", phone: "", active: true });
  const [roleForm, setRoleForm] = useState({ name: "", permissions: {} });
  const [stageForm, setStageForm] = useState({ name: "", color: "emerald" });
  const [ruleForm, setRuleForm] = useState({ doc_type: "PO", min_amount: 0, approver_role: "Direktur", level: 1 });
  const [branches, setBranches] = useState([]);
  const [newBranch, setNewBranch] = useState("");
  const [actionRules, setActionRules] = useState([]);
  const [locations, setLocations] = useState([]);
  const [locUser, setLocUser] = useState(null);
  const [viewProfile, setViewProfile] = useState(null);
  const [locSel, setLocSel] = useState(null);
  const [company, setCompany] = useState({ nama_perusahaan: "PT Abad Jaya Wampu", nama_wakil_developer: "", nomor_rekening: "", alamat: "", nik_developer: "", jabatan_wakil: "Direktur" });
  const [attHours, setAttHours] = useState({ start: "07:00", end: "17:00", tolerance_min: 0 });
  const [addons, setAddons] = useState([]);
  const [newAddon, setNewAddon] = useState({ name: "", price: "" });
  const [bfFields, setBfFields] = useState([]);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [newAcct, setNewAcct] = useState({ bank: "", number: "", holder: "" });

  const load = useCallback(() => {
    api.get("/users").then((r) => setUsers(r.data));
    api.get("/roles").then((r) => setRoles(r.data));
    api.get("/crm/stages").then((r) => setStages(r.data));
    api.get("/approvals/rules").then((r) => setRules(r.data)).catch(() => {});
    api.get("/meta/modules").then((r) => setMeta(r.data));
    api.get("/attendance/geofence").then((r) => setGeofence(r.data)).catch(() => {});
    api.get("/settings/addons").then((r) => setAddons(r.data)).catch(() => {});
    api.get("/settings/bank-branches").then((r) => setBranches(r.data.branches)).catch(() => {});
    api.get("/approval-action-rules").then((r) => setActionRules(r.data)).catch(() => {});
    api.get("/locations").then((r) => setLocations(r.data)).catch(() => {});
    api.get("/settings/company-info").then((r) => setCompany((c) => ({ ...c, ...r.data }))).catch(() => {});
    api.get("/settings/attendance").then((r) => setAttHours((a) => ({ ...a, ...r.data }))).catch(() => {});
    api.get("/settings/booking-form-fields").then((r) => setBfFields(r.data.fields)).catch(() => {});
    api.get("/settings/bank-accounts").then((r) => setBankAccounts(r.data.accounts)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const wrap = (fn, okMsg, closer) => async () => {
    try { await fn(); toast.success(okMsg); closer && closer(); load(); } catch (e) { toast.error(errMsg(e)); }
  };

  const togglePerm = (module, action) => {
    const perms = { ...roleForm.permissions };
    const cur = new Set(perms[module] || []);
    if (cur.has(action)) cur.delete(action); else cur.add(action);
    perms[module] = [...cur];
    setRoleForm({ ...roleForm, permissions: perms });
  };

  const TABS = [["users", "Pengguna"], ["roles", "Role & RBAC Matrix"], ["universal", "Universal Approval"], ["pipeline", "Pipeline CRM"], ["rules", "Aturan Approval"], ["bank", "Cabang Bank"], ["rekening", "Rekening Resmi"], ["formbooking", "Formulir Booking"], ["addons", "Add-on Booking"], ["geofence", "Geofence Absensi"], ["jam", "Jam Absensi"]];

  return (
    <div data-testid="settings-page">
      <PageHeader title="Pengaturan & RBAC" subtitle="Kelola pengguna, role dinamis, hak akses per modul/aksi, pipeline, dan aturan approval" testid="settings-header" />

      <div className="flex gap-2 mb-5 flex-wrap">
        {TABS.map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`settings-tab-${k}`}
            className={`text-xs sm:text-sm px-3.5 py-2 rounded-lg border transition-colors ${tab === k ? "bg-emerald-600/10 text-emerald-300 border-emerald-500/50" : "text-slate-400 border-slate-800 hover:bg-slate-900 hover:text-slate-200"}`}>
            {l}
          </button>
        ))}
      </div>

      {tab === "jam" && (
        <Card testid="attendance-hours-card">
          {user?.role === "Super Admin" ? (
            <div className="space-y-4 max-w-md">
              <p className="text-xs text-slate-400">Jam kerja untuk penilaian absensi (zona WIB / GMT+7). Absen masuk setelah jam masuk + toleransi tercatat <span className="text-rose-300">Terlambat</span>; absen pulang sebelum jam pulang tercatat <span className="text-amber-300">Pulang Cepat</span>. Berlaku untuk semua pegawai.</p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <Field label="Jam Masuk"><input type="time" className={inputCls} value={attHours.start} onChange={(e) => setAttHours({ ...attHours, start: e.target.value })} data-testid="att-start-input" /></Field>
                <Field label="Jam Pulang"><input type="time" className={inputCls} value={attHours.end} onChange={(e) => setAttHours({ ...attHours, end: e.target.value })} data-testid="att-end-input" /></Field>
                <Field label="Toleransi (menit)"><input type="number" min="0" className={inputCls} value={attHours.tolerance_min} onChange={(e) => setAttHours({ ...attHours, tolerance_min: +e.target.value })} data-testid="att-tolerance-input" /></Field>
              </div>
              <Btn onClick={wrap(() => api.put("/settings/attendance", attHours), "Pengaturan jam absen disimpan")} data-testid="att-hours-save">Simpan Pengaturan</Btn>
            </div>
          ) : (
            <p className="text-sm text-slate-500" data-testid="att-hours-readonly">Jam kerja saat ini: masuk <b className="text-slate-300">{attHours.start}</b>, pulang <b className="text-slate-300">{attHours.end}</b>, toleransi <b className="text-slate-300">{attHours.tolerance_min} menit</b>. Perubahan hanya dapat dilakukan oleh Super Admin.</p>
          )}
        </Card>
      )}

      {tab === "users" && (
        <div>
          <div className="mb-4">
            {hasPerm("settings", "create") && <Btn onClick={() => { setEditingUser(null); setUserForm({ name: "", email: "", password: "", role: roles[0]?.name || "Sales", phone: "", active: true }); setShowUser(true); }} data-testid="add-user-button"><Plus className="w-4 h-4" /> Tambah Pengguna</Btn>}
          </div>
          <div className="overflow-x-auto rounded-xl border border-slate-800" data-testid="users-table">
            <table className="w-full text-sm">
              <thead><tr className="bg-slate-900 border-b border-slate-800">{["Nama", "Email", "Role", "Status", "Akses Lokasi", "Aksi"].map((h) => <th key={h} className="text-left text-xs font-mono uppercase tracking-wider text-slate-500 px-4 py-3">{h}</th>)}</tr></thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id} className="border-b border-slate-800/60 hover:bg-slate-900/70" data-testid={`user-row-${u.email}`}>
                    <td className="px-4 py-3 text-slate-200">{u.name}</td>
                    <td className="px-4 py-3 text-slate-400 font-mono text-xs">{u.email}</td>
                    <td className="px-4 py-3"><Badge tone={u.role === "Super Admin" ? "emerald" : "cyan"}>{u.role}</Badge></td>
                    <td className="px-4 py-3">{u.active ? <Badge tone="emerald">Aktif</Badge> : <Badge tone="rose">Nonaktif</Badge>}</td>
                    <td className="px-4 py-3">
                      {u.role === "Super Admin" ? <span className="text-[10px] font-mono text-emerald-400">SEMUA LOKASI</span>
                        : u.allowed_locations ? <Badge tone="amber">{u.allowed_locations.length} lokasi</Badge>
                        : <span className="text-[10px] text-slate-500">tanpa batasan</span>}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex gap-1.5">
                        {["Super Admin", "Global Admin", "Direktur"].includes(user?.role) && <Btn tone="ghost" className="!px-2.5 !py-1 !text-xs" onClick={async () => { try { const r = await api.get(`/users/${u.id}/profile`); setViewProfile(r.data); } catch (e) { toast.error(errMsg(e)); } }} data-testid={`user-profile-${u.email}`}>Profil</Btn>}
                        {hasPerm("settings", "edit") && <Btn tone="ghost" className="!px-2.5 !py-1 !text-xs" onClick={() => { setLocUser(u); setLocSel(u.allowed_locations ?? null); }} data-testid={`user-locations-${u.email}`}>Lokasi</Btn>}
                        {hasPerm("settings", "edit") && <Btn tone="ghost" className="!px-2.5 !py-1 !text-xs" onClick={() => { setEditingUser(u); setUserForm({ name: u.name, email: u.email, password: "", role: u.role, phone: u.phone || "", active: u.active }); setShowUser(true); }} data-testid={`user-edit-${u.email}`}>Edit</Btn>}
                        {hasPerm("settings", "delete") && u.role !== "Super Admin" && <Btn tone="danger" className="!px-2.5 !py-1 !text-xs" onClick={wrap(() => api.delete(`/users/${u.id}`), "Pengguna dihapus")} data-testid={`user-delete-${u.email}`}><Trash2 className="w-3 h-3" /></Btn>}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === "roles" && (
        <div>
          <div className="mb-4">
            {hasPerm("settings", "create") && <Btn onClick={() => { setEditingRole(null); setRoleForm({ name: "", permissions: {} }); setShowRole(true); }} data-testid="add-role-button"><Plus className="w-4 h-4" /> Tambah Role</Btn>}
          </div>
          <div className="overflow-x-auto rounded-xl border border-slate-800" data-testid="rbac-matrix">
            <table className="w-full text-xs">
              <thead>
                <tr className="bg-slate-900 border-b border-slate-800">
                  <th className="text-left font-mono uppercase tracking-wider text-slate-500 px-4 py-3 sticky left-0 bg-slate-900">Modul</th>
                  {roles.map((r) => <th key={r.id} className="text-center font-mono uppercase tracking-wider text-slate-500 px-3 py-3 min-w-[110px]">{r.name}</th>)}
                </tr>
              </thead>
              <tbody>
                {meta.modules.map((m) => (
                  <tr key={m} className="border-b border-slate-800/60" data-testid={`rbac-row-${m}`}>
                    <td className="px-4 py-2.5 text-slate-300 font-medium sticky left-0 bg-slate-950">{MODULE_LABELS[m] || m}</td>
                    {roles.map((r) => {
                      const perms = r.permissions?.[m] || [];
                      const isSA = r.name === "Super Admin";
                      return (
                        <td key={r.id} className="px-3 py-2.5 text-center">
                          {isSA ? (
                            <span className="text-emerald-400 text-[10px] font-mono flex items-center justify-center gap-1"><ShieldCheck className="w-3 h-3" /> FULL</span>
                          ) : (
                            <div className="flex justify-center gap-1 flex-wrap">
                              {perms.length === 0 ? <span className="text-slate-600">—</span> : perms.map((a) => (
                                <span key={a} className={`text-[9px] font-mono px-1.5 py-0.5 rounded border ${a === "approve" || a === "sign" ? "border-amber-500/40 text-amber-300" : "border-emerald-500/40 text-emerald-300"}`}>{ACTION_LABELS[a] || a}</span>
                              ))}
                            </div>
                          )}
                          {r.name !== "Super Admin" && hasPerm("settings", "edit") && (
                            <button className="block mx-auto mt-1 text-[10px] text-slate-500 hover:text-emerald-300 underline" onClick={() => { setEditingRole(r); setRoleForm({ name: r.name, permissions: r.permissions || {} }); setShowRole(true); }} data-testid={`rbac-edit-${r.name.replace(/\s/g, "-")}-${m}`}>atur</button>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === "pipeline" && (
        <div>
          <div className="flex gap-2 mb-4 items-end flex-wrap">
            <Field label="Nama Tahap Baru"><input className={inputCls} value={stageForm.name} onChange={(e) => setStageForm({ ...stageForm, name: e.target.value })} data-testid="stage-name-input" /></Field>
            <Field label="Warna">
              <select className={inputCls} value={stageForm.color} onChange={(e) => setStageForm({ ...stageForm, color: e.target.value })} data-testid="stage-color-select">
                {["emerald", "cyan", "indigo", "amber", "orange", "rose"].map((c) => <option key={c}>{c}</option>)}
              </select>
            </Field>
            <Btn onClick={wrap(() => api.post("/crm/stages", { ...stageForm, order: stages.length + 1 }), "Tahap ditambahkan", () => setStageForm({ name: "", color: "emerald" }))} data-testid="stage-add-button"><Plus className="w-4 h-4" /> Tambah</Btn>
          </div>
          <div className="space-y-2" data-testid="stages-list">
            {stages.map((s) => (
              <Card key={s.id} className="!p-3 flex items-center justify-between" testid={`stage-${s.id}`}>
                <div className="flex items-center gap-3">
                  <span className="font-mono text-xs text-slate-500">#{s.order}</span>
                  <Badge tone={s.color}>{s.name}</Badge>
                </div>
                {hasPerm("settings", "delete") && <Btn tone="danger" className="!px-2.5 !py-1 !text-xs" onClick={wrap(() => api.delete(`/crm/stages/${s.id}`), "Tahap dihapus")} data-testid={`stage-delete-${s.id}`}><Trash2 className="w-3 h-3" /></Btn>}
              </Card>
            ))}
          </div>
        </div>
      )}

      {tab === "rules" && (
        <div>
          <Card className="mb-4" testid="rule-form-card">
            <div className="text-xs font-mono uppercase tracking-wider text-slate-400 mb-3">Tambah Aturan Approval</div>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3 items-end">
              <Field label="Tipe Dokumen">
                <select className={inputCls} value={ruleForm.doc_type} onChange={(e) => setRuleForm({ ...ruleForm, doc_type: e.target.value })} data-testid="rule-doctype-select">
                  <option value="PO">Purchase Order</option><option value="MATERIAL_REQUEST">Request Material</option><option value="SPK_PROYEK">SPK Proyek Konstruksi</option>
                </select>
              </Field>
              <Field label="Nominal Min (Rp)"><input type="number" className={inputCls} value={ruleForm.min_amount} onChange={(e) => setRuleForm({ ...ruleForm, min_amount: +e.target.value })} data-testid="rule-minamount-input" /></Field>
              <Field label="Approver Role">
                <select className={inputCls} value={ruleForm.approver_role} onChange={(e) => setRuleForm({ ...ruleForm, approver_role: e.target.value })} data-testid="rule-role-select">
                  {roles.map((r) => <option key={r.id}>{r.name}</option>)}
                </select>
              </Field>
              <Field label="Level"><input type="number" className={inputCls} value={ruleForm.level} onChange={(e) => setRuleForm({ ...ruleForm, level: +e.target.value })} data-testid="rule-level-input" /></Field>
              <Btn onClick={wrap(() => api.post("/approvals/rules", ruleForm), "Aturan ditambahkan")} data-testid="rule-add-button"><Plus className="w-4 h-4" /> Tambah</Btn>
            </div>
          </Card>
          <div className="space-y-2" data-testid="rules-list">
            {rules.map((r) => (
              <Card key={r.id} className="!p-3 flex items-center justify-between" testid={`rule-${r.id}`}>
                <div className="flex items-center gap-3 text-sm">
                  <Badge tone="cyan">{r.doc_type}</Badge>
                  <span className="text-slate-300">≥ <span className="font-mono">{fmtIDR(r.min_amount)}</span></span>
                  <span className="text-slate-500">→ Level {r.level}:</span>
                  <Badge tone="amber">{r.approver_role}</Badge>
                </div>
                {hasPerm("settings", "delete") && <Btn tone="danger" className="!px-2.5 !py-1 !text-xs" onClick={wrap(() => api.delete(`/approvals/rules/${r.id}`), "Aturan dihapus")} data-testid={`rule-delete-${r.id}`}><Trash2 className="w-3 h-3" /></Btn>}
              </Card>
            ))}
          </div>
        </div>
      )}

      {tab === "universal" && (
        <div>
          <Card className="mb-4" testid="universal-info">
            <p className="text-xs text-slate-400">Aktifkan toggle agar aksi <b className="text-amber-300">ditahan (Pending)</b> dan baru tereksekusi setelah di-approve oleh role penentu di modul Approve & Sign. Tugas otomatis terkirim ke To Do List approver.</p>
          </Card>
          <div className="space-y-2" data-testid="action-rules-list">
            {actionRules.map((r) => (
              <Card key={r.action_key} className="!p-4" testid={`action-rule-${r.action_key}`}>
                <div className="flex flex-wrap items-center gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={r.enabled}
                      onChange={(e) => setActionRules(actionRules.map((x) => x.action_key === r.action_key ? { ...x, enabled: e.target.checked } : x))}
                      className="w-4 h-4 accent-emerald-500" data-testid={`rule-toggle-${r.action_key}`} />
                    <div>
                      <div className="text-sm text-slate-100">{r.label}</div>
                      <Badge tone="cyan">{r.module}</Badge>
                    </div>
                  </label>
                  <div className="flex items-center gap-2 ml-auto">
                    {r.threshold_label && (
                      <div className="flex items-center gap-1.5">
                        <span className="text-[10px] font-mono text-slate-500">{r.threshold_label}</span>
                        <input type="number" className="w-20 bg-slate-950 border border-slate-700 rounded-lg px-2 py-1.5 text-xs text-slate-100"
                          value={r.threshold_value} onChange={(e) => setActionRules(actionRules.map((x) => x.action_key === r.action_key ? { ...x, threshold_value: +e.target.value } : x))}
                          data-testid={`rule-threshold-${r.action_key}`} />
                      </div>
                    )}
                    <span className="text-[10px] font-mono text-slate-500">Approver:</span>
                    <select className="bg-slate-950 border border-slate-700 rounded-lg px-2 py-1.5 text-xs text-slate-100"
                      value={r.approver_role}
                      onChange={(e) => setActionRules(actionRules.map((x) => x.action_key === r.action_key ? { ...x, approver_role: e.target.value } : x))}
                      data-testid={`rule-role-${r.action_key}`}>
                      {roles.map((ro) => <option key={ro.id}>{ro.name}</option>)}
                    </select>
                    <Btn className="!px-2.5 !py-1.5 !text-xs" onClick={wrap(() => api.put(`/approval-action-rules/${r.action_key}`, r), `Aturan '${r.label}' disimpan`)} data-testid={`rule-save-${r.action_key}`}>Simpan</Btn>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      )}

      {tab === "bank" && (
        <div className="max-w-lg">
          <Card testid="bank-branches-card">
            <div className="text-xs font-mono uppercase tracking-wider text-slate-400 mb-3">Daftar Cabang Bank (untuk Formulir Booking & TTD Bank)</div>
            <div className="flex gap-2 mb-3 flex-wrap">
              <input className={inputCls} placeholder="Nama cabang baru..." value={newBranch} onChange={(e) => setNewBranch(e.target.value)} data-testid="branch-name-input" />
              <Btn onClick={wrap(() => api.put("/settings/bank-branches", { branches: [...branches, newBranch] }), "Cabang ditambahkan", () => setNewBranch(""))} data-testid="branch-add-button" disabled={!newBranch.trim()}><Plus className="w-4 h-4" /> Tambah</Btn>
            </div>
            <div className="space-y-2" data-testid="branches-list">
              {branches.map((b) => (
                <div key={b} className="flex items-center justify-between bg-slate-950 border border-slate-800 rounded-lg px-3 py-2" data-testid={`branch-${b}`}>
                  <span className="text-sm text-slate-200">{b}</span>
                  {hasPerm("settings", "edit") && (
                    <Btn tone="danger" className="!px-2.5 !py-1 !text-xs" onClick={wrap(() => api.put("/settings/bank-branches", { branches: branches.filter((x) => x !== b) }), "Cabang dihapus")} data-testid={`branch-delete-${b}`}><Trash2 className="w-3 h-3" /></Btn>
                  )}
                </div>
              ))}
              {branches.length === 0 && <p className="text-slate-500 text-sm">Belum ada cabang.</p>}
            </div>
          </Card>
          <Card className="mt-4" testid="company-info-card">
            <div className="text-xs font-mono uppercase tracking-wider text-slate-400 mb-3">Data Perusahaan (untuk variabel dokumen: wakil developer, rekening, NIK)</div>
            <div className="space-y-3">
              <Field label="Nama Perusahaan"><input className={inputCls} value={company.nama_perusahaan} onChange={(e) => setCompany({ ...company, nama_perusahaan: e.target.value })} data-testid="company-name-input" /></Field>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Field label="Nama Wakil Pihak Developer"><input className={inputCls} value={company.nama_wakil_developer} onChange={(e) => setCompany({ ...company, nama_wakil_developer: e.target.value })} data-testid="company-wakil-input" /></Field>
                <Field label="Jabatan Wakil"><input className={inputCls} value={company.jabatan_wakil} onChange={(e) => setCompany({ ...company, jabatan_wakil: e.target.value })} data-testid="company-jabatan-input" /></Field>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Field label="Nomor Rekening"><input className={inputCls} value={company.nomor_rekening} onChange={(e) => setCompany({ ...company, nomor_rekening: e.target.value })} data-testid="company-rekening-input" /></Field>
                <Field label="NIK Developer"><input className={inputCls} value={company.nik_developer} onChange={(e) => setCompany({ ...company, nik_developer: e.target.value })} data-testid="company-nik-input" /></Field>
              </div>
              <Field label="Alamat Developer"><input className={inputCls} value={company.alamat} onChange={(e) => setCompany({ ...company, alamat: e.target.value })} data-testid="company-alamat-input" /></Field>
              <Btn onClick={wrap(() => api.put("/settings/company-info", company), "Data perusahaan disimpan")} data-testid="company-save-button">Simpan Data Perusahaan</Btn>
            </div>
          </Card>
        </div>
      )}

      {tab === "rekening" && (
        <div className="max-w-lg">
          <Card testid="bank-accounts-card">
            <div className="text-xs font-mono uppercase tracking-wider text-slate-400 mb-3">Rekening Resmi PT Abad Jaya Wampu (tampil di aplikasi ABAD JAYA LIVING via GET /sync/rekening)</div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-3">
              <input className={inputCls} placeholder="Bank (mis. BCA)" value={newAcct.bank} onChange={(e) => setNewAcct({ ...newAcct, bank: e.target.value })} data-testid="acct-bank-input" />
              <input className={inputCls} placeholder="No. rekening" value={newAcct.number} onChange={(e) => setNewAcct({ ...newAcct, number: e.target.value })} data-testid="acct-number-input" />
              <input className={inputCls} placeholder="Atas nama" value={newAcct.holder} onChange={(e) => setNewAcct({ ...newAcct, holder: e.target.value })} data-testid="acct-holder-input" />
            </div>
            <Btn onClick={wrap(() => api.put("/settings/bank-accounts", { accounts: [...bankAccounts, newAcct] }), "Rekening ditambahkan", () => setNewAcct({ bank: "", number: "", holder: "" }))} data-testid="acct-add-button" disabled={!newAcct.bank.trim() || !newAcct.number.trim() || !newAcct.holder.trim()}><Plus className="w-4 h-4" /> Tambah Rekening</Btn>
            <div className="space-y-2 mt-3" data-testid="accounts-list">
              {bankAccounts.map((a, i) => (
                <div key={i} className="flex items-center justify-between bg-slate-950 border border-slate-800 rounded-lg px-3 py-2" data-testid={`acct-${i}`}>
                  <span className="text-sm text-slate-200">{a.bank} · <span className="font-mono">{a.number}</span> · {a.holder}</span>
                  {hasPerm("settings", "edit") && (
                    <Btn tone="danger" className="!px-2.5 !py-1 !text-xs" onClick={wrap(() => api.put("/settings/bank-accounts", { accounts: bankAccounts.filter((_, j) => j !== i) }), "Rekening dihapus")} data-testid={`acct-delete-${i}`}><Trash2 className="w-3 h-3" /></Btn>
                  )}
                </div>
              ))}
              {bankAccounts.length === 0 && <p className="text-slate-500 text-sm">Belum ada rekening resmi.</p>}
            </div>
          </Card>
        </div>
      )}

      {tab === "formbooking" && (
        <div className="max-w-3xl">
          <Card testid="formbooking-card">
            <div className="text-xs font-mono uppercase tracking-wider text-slate-400 mb-3">Isian Formulir Booking Resmi (dibaca aplikasi via GET /sync/booking-form; profile_key untuk isi-otomatis dari profil konsumen)</div>
            <div className="space-y-2" data-testid="formbooking-list">
              {bfFields.map((f, i) => (
                <div key={i} className="grid grid-cols-2 sm:grid-cols-[1fr_1fr_120px_90px_1fr_36px] gap-2 items-center bg-slate-950 border border-slate-800 rounded-lg p-2" data-testid={`bffield-${i}`}>
                  <input className={inputCls} placeholder="key" value={f.key} onChange={(e) => setBfFields(bfFields.map((x, j) => j === i ? { ...x, key: e.target.value } : x))} data-testid={`bffield-key-${i}`} />
                  <input className={inputCls} placeholder="Label" value={f.label} onChange={(e) => setBfFields(bfFields.map((x, j) => j === i ? { ...x, label: e.target.value } : x))} data-testid={`bffield-label-${i}`} />
                  <select className={inputCls} value={f.type} onChange={(e) => setBfFields(bfFields.map((x, j) => j === i ? { ...x, type: e.target.value } : x))} data-testid={`bffield-type-${i}`}>
                    {["text", "textarea", "number", "date", "email", "nik", "select"].map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                  <label className="text-xs text-slate-400 flex items-center gap-1.5">
                    <input type="checkbox" checked={!!f.required} onChange={(e) => setBfFields(bfFields.map((x, j) => j === i ? { ...x, required: e.target.checked } : x))} data-testid={`bffield-required-${i}`} /> wajib
                  </label>
                  <input className={inputCls} placeholder="profile_key / opsi (koma)" value={f.type === "select" ? (f.options || []).join(",") : (f.profile_key || "")}
                    onChange={(e) => setBfFields(bfFields.map((x, j) => j === i ? (f.type === "select" ? { ...x, options: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) } : { ...x, profile_key: e.target.value }) : x))}
                    data-testid={`bffield-extra-${i}`} />
                  <button onClick={() => setBfFields(bfFields.filter((_, j) => j !== i))} className="text-rose-400" data-testid={`bffield-del-${i}`}><Trash2 className="w-4 h-4" /></button>
                </div>
              ))}
            </div>
            <div className="flex gap-2 mt-3 flex-wrap">
              <Btn onClick={() => setBfFields([...bfFields, { key: "", label: "", type: "text", required: false, options: [], profile_key: "" }])} data-testid="bffield-add"><Plus className="w-4 h-4" /> Tambah Isian</Btn>
              <Btn onClick={wrap(() => api.put("/settings/booking-form-fields", { fields: bfFields }), "Formulir booking disimpan")} data-testid="bffield-save">Simpan Formulir</Btn>
            </div>
          </Card>
        </div>
      )}

      {tab === "addons" && (
        <div className="max-w-lg">
          <Card testid="addons-card">
            <div className="text-xs font-mono uppercase tracking-wider text-slate-400 mb-3">Master Add-on Booking (mis. Kanopi, Pagar, Taman) — dipilih di Formulir Booking & menambah harga jual</div>
            <div className="flex gap-2 mb-3 flex-wrap">
              <input className={inputCls} placeholder="Nama add-on..." value={newAddon.name} onChange={(e) => setNewAddon({ ...newAddon, name: e.target.value })} data-testid="addon-name-input" />
              <input className={inputCls + " !w-36"} type="number" placeholder="Harga (Rp)" value={newAddon.price} onChange={(e) => setNewAddon({ ...newAddon, price: e.target.value })} data-testid="addon-price-input" />
              <Btn onClick={wrap(() => api.put("/settings/addons", { addons: [...addons, { name: newAddon.name, price: +newAddon.price || 0 }] }), "Add-on ditambahkan", () => setNewAddon({ name: "", price: "" }))} data-testid="addon-add-button" disabled={!newAddon.name.trim()}><Plus className="w-4 h-4" /> Tambah</Btn>
            </div>
            <div className="space-y-2" data-testid="addons-list">
              {addons.map((a) => (
                <div key={a.id} className="flex items-center justify-between bg-slate-950 border border-slate-800 rounded-lg px-3 py-2" data-testid={`addon-${a.id}`}>
                  <span className="text-sm text-slate-200">{a.name}</span>
                  <div className="flex items-center gap-3">
                    <span className="text-xs font-mono text-emerald-400">{fmtIDR(a.price)}</span>
                    {hasPerm("settings", "edit") && (
                      <Btn tone="danger" className="!px-2.5 !py-1 !text-xs" onClick={wrap(() => api.put("/settings/addons", { addons: addons.filter((x) => x.id !== a.id) }), "Add-on dihapus")} data-testid={`addon-delete-${a.id}`}><Trash2 className="w-3 h-3" /></Btn>
                    )}
                  </div>
                </div>
              ))}
              {addons.length === 0 && <p className="text-slate-500 text-sm">Belum ada add-on.</p>}
            </div>
          </Card>
        </div>
      )}

      {tab === "geofence" && (
        <div className="max-w-lg space-y-4">
          <Card testid="geofence-note">
            <p className="text-xs text-slate-400">Geofence per proyek kini diatur melalui menu <b className="text-emerald-400">Proyek → Tambah/Edit Proyek</b> dengan peta interaktif. Pengaturan di bawah adalah <b>fallback global (Kantor Pusat)</b> untuk absensi tanpa proyek.</p>
          </Card>
          <Card testid="geofence-form-card">
          <div className="space-y-3">
            <Field label="Label Lokasi"><input className={inputCls} value={geofence.label || ""} onChange={(e) => setGeofence({ ...geofence, label: e.target.value })} data-testid="geofence-label-input" /></Field>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <Field label="Latitude"><input type="number" step="0.000001" className={inputCls} value={geofence.lat} onChange={(e) => setGeofence({ ...geofence, lat: +e.target.value })} data-testid="geofence-lat-input" /></Field>
              <Field label="Longitude"><input type="number" step="0.000001" className={inputCls} value={geofence.lng} onChange={(e) => setGeofence({ ...geofence, lng: +e.target.value })} data-testid="geofence-lng-input" /></Field>
              <Field label="Radius (meter)"><input type="number" className={inputCls} value={geofence.radius_m} onChange={(e) => setGeofence({ ...geofence, radius_m: +e.target.value })} data-testid="geofence-radius-input" /></Field>
            </div>
            <Btn onClick={wrap(() => api.put("/attendance/geofence", geofence), "Geofence diperbarui")} data-testid="geofence-save-button">Simpan Geofence</Btn>
          </div>
        </Card>
        </div>
      )}

      <Modal open={!!viewProfile} onClose={() => setViewProfile(null)} title={viewProfile ? `Profil — ${viewProfile.name}` : ""} testid="user-profile-modal">
        {viewProfile && (
          <div className="text-sm" data-testid="user-profile-content">
            {[["Email", viewProfile.email], ["Role", viewProfile.role],
              ["No. WhatsApp", viewProfile.phone_wa || viewProfile.phone],
              ["NIK", viewProfile.identitas?.nik],
              ["Tempat Lahir", viewProfile.identitas?.tempat_lahir],
              ["Tanggal Lahir", viewProfile.identitas?.tanggal_lahir || viewProfile.identitas?.ttl],
              ["Alamat", viewProfile.alamat],
              ["Pasangan", viewProfile.pasangan?.nama],
              ["Jumlah Anak", (viewProfile.anak || []).length || "-"],
              ["Bank Gaji", viewProfile.rekening_gaji?.bank ? `${viewProfile.rekening_gaji.bank} — ${viewProfile.rekening_gaji.no_rekening || "-"}` : null],
              ["Foto Profil", viewProfile.has_photo ? "Sudah diunggah" : "Belum ada"],
            ].map(([k, v]) => (
              <div key={k} className="flex gap-3 py-1.5 border-b border-slate-800/60 last:border-0">
                <span className="w-32 shrink-0 text-[10px] font-mono uppercase tracking-wider text-slate-500 pt-0.5">{k}</span>
                <span className="text-xs text-slate-200">{v || "-"}</span>
              </div>
            ))}
          </div>
        )}
      </Modal>

      <Modal open={!!locUser} onClose={() => setLocUser(null)} title={locUser ? `Akses Lokasi — ${locUser.name}` : ""} testid="user-locations-modal">
        {locUser && (
          <div className="space-y-3">
            <label className="flex items-center gap-2 text-sm text-slate-200 cursor-pointer">
              <input type="checkbox" checked={locSel === null} onChange={(e) => setLocSel(e.target.checked ? null : [])} className="w-4 h-4 accent-emerald-500" data-testid="loc-unrestricted-toggle" />
              Tanpa batasan (akses semua lokasi)
            </label>
            {locSel !== null && (
              <div className="space-y-2 pl-6" data-testid="loc-checkbox-list">
                {locations.map((l) => (
                  <label key={l.id} className="flex items-center gap-2 text-sm text-slate-300 cursor-pointer">
                    <input type="checkbox" checked={locSel.includes(l.id)}
                      onChange={(e) => setLocSel(e.target.checked ? [...locSel, l.id] : locSel.filter((x) => x !== l.id))}
                      className="w-4 h-4 accent-emerald-500" data-testid={`loc-check-${l.id}`} />
                    {l.name}
                  </label>
                ))}
                {locations.length === 0 && <p className="text-slate-500 text-xs">Belum ada lokasi.</p>}
              </div>
            )}
            <div className="flex justify-end gap-2 pt-2">
              <Btn tone="ghost" onClick={() => setLocUser(null)}>Batal</Btn>
              <Btn onClick={wrap(() => api.put(`/users/${locUser.id}/locations`, { location_ids: locSel }), "Akses lokasi disimpan", () => setLocUser(null))} data-testid="loc-save-button">Simpan Akses</Btn>
            </div>
          </div>
        )}
      </Modal>

      <Modal open={showUser} onClose={() => setShowUser(false)} title={editingUser ? "Edit Pengguna" : "Tambah Pengguna"} testid="user-form-modal">
        <div className="space-y-3">
          <Field label="Nama"><input className={inputCls} value={userForm.name} onChange={(e) => setUserForm({ ...userForm, name: e.target.value })} data-testid="user-name-input" /></Field>
          <Field label="Email"><input type="email" className={inputCls} value={userForm.email} onChange={(e) => setUserForm({ ...userForm, email: e.target.value })} data-testid="user-email-input" /></Field>
          <Field label={editingUser ? "Password (kosongkan jika tidak diubah)" : "Password"}><input type="password" className={inputCls} value={userForm.password} onChange={(e) => setUserForm({ ...userForm, password: e.target.value })} data-testid="user-password-input" /></Field>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Role">
              <select className={inputCls} value={userForm.role} onChange={(e) => setUserForm({ ...userForm, role: e.target.value })} data-testid="user-role-select">
                {roles.map((r) => <option key={r.id}>{r.name}</option>)}
              </select>
            </Field>
            <Field label="Status">
              <select className={inputCls} value={userForm.active ? "1" : "0"} onChange={(e) => setUserForm({ ...userForm, active: e.target.value === "1" })} data-testid="user-active-select">
                <option value="1">Aktif</option><option value="0">Nonaktif</option>
              </select>
            </Field>
            <Field label="Jabatan"><input className={inputCls} value={userForm.jabatan || ""} onChange={(e) => setUserForm({ ...userForm, jabatan: e.target.value })} placeholder="Contoh: Site Manager Cluster A" data-testid="user-jabatan-input" /></Field>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Btn tone="ghost" onClick={() => setShowUser(false)}>Batal</Btn>
            <Btn onClick={wrap(() => editingUser ? api.put(`/users/${editingUser.id}`, userForm) : api.post("/users", userForm), "Pengguna disimpan", () => setShowUser(false))} data-testid="user-save-button">Simpan</Btn>
          </div>
        </div>
      </Modal>

      <Modal open={showRole} onClose={() => setShowRole(false)} title={editingRole ? `Atur Role: ${editingRole.name}` : "Role Baru"} wide testid="role-form-modal">
        <div className="space-y-4">
          {!editingRole && <Field label="Nama Role"><input className={inputCls} value={roleForm.name} onChange={(e) => setRoleForm({ ...roleForm, name: e.target.value })} data-testid="role-name-input" /></Field>}
          <div className="overflow-x-auto rounded-lg border border-slate-800">
            <table className="w-full text-xs">
              <thead>
                <tr className="bg-slate-900 border-b border-slate-800">
                  <th className="text-left font-mono uppercase tracking-wider text-slate-500 px-3 py-2">Modul</th>
                  {meta.actions.map((a) => <th key={a} className="text-center font-mono uppercase tracking-wider text-slate-500 px-2 py-2">{ACTION_LABELS[a]}</th>)}
                </tr>
              </thead>
              <tbody>
                {meta.modules.map((m) => (
                  <tr key={m} className="border-b border-slate-800/60">
                    <td className="px-3 py-2 text-slate-300">{MODULE_LABELS[m] || m}</td>
                    {meta.actions.map((a) => (
                      <td key={a} className="text-center px-2 py-2">
                        <input
                          type="checkbox"
                          checked={(roleForm.permissions[m] || []).includes(a)}
                          onChange={() => togglePerm(m, a)}
                          data-testid={`rbac-checkbox-${m}-${a}`}
                          className="w-4 h-4 accent-emerald-500 cursor-pointer"
                        />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex justify-end gap-2">
            <Btn tone="ghost" onClick={() => setShowRole(false)}>Batal</Btn>
            <Btn onClick={wrap(() => editingRole ? api.put(`/roles/${editingRole.id}`, roleForm) : api.post("/roles", roleForm), "Role disimpan", () => setShowRole(false))} data-testid="role-save-button">Simpan Role</Btn>
          </div>
        </div>
      </Modal>
    </div>
  );
}
